<template>
  <div id="app-container" class="td-600" :class="appContainerClasses">
    <div v-if="!selectedLanguage" class="container">
      <div class="language-selector-container">
        <div class="row logo-container">
          <no-drag-image src="@/assets/logo-regular.png" />
        </div>

        <div class="row g-4">
          <div v-for="(locale, i) in locales" :key="locale" class="col-6">
            <div class="language-button" :class="`language-button-${i}`" @click="selectLanguage(locale)">
              <no-drag-image :src="flagImages[locale]" @click="selectLanguage(locale)" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div
        ref="foreground-overlay"
        class="foreground-overlay"
        :class="showForeground ? '' : 'd-none'"
      />

      <router-view />
    </div>

    <vue3-snackbar :duration="4000" right top />
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import Velocity from 'velocity-animate';

import locales from '@/lib/i18n/index.js';
import NoDragImage from './components/common/NoDragImage';

import flagEn from './assets/flags/gb.svg';
import flagDa from './assets/flags/dk.svg';
import flagSe from './assets/flags/se.svg';
import flagNo from './assets/flags/no.svg';

const flagImages = {
  en: flagEn,
  da: flagDa,
  se: flagSe,
  no: flagNo,
};

export default {
  name: 'App',
  components: { NoDragImage },
  data() {
    return {
      showForeground: true,
      currentAnimating: false,

      flagImages,
      selectedLanguage: false,
      locales: Object.keys(locales).filter((v) => v !== 'system'),
    };
  },
  watch: {
    background() {
      this.showForeground = true;

      this.currentAnimating = true;

      const foreground = this.$refs['foreground-overlay'];

      this.$nextTick(() => {
        console.log('foreground', foreground);

        foreground.style.opacity = '1';

        Velocity(foreground, 'stop');

        Velocity(foreground, {
          opacity: 0,
        }, {
          easing: 'easeInOut',
          duration: 600 * this.transitionDurationScale,
        }).then(() => {
          this.showForeground = false;
        });
      });
    },
  },
  computed: {
    ...mapState(useSurveyStore, ['background', 'submissionToken', 'cliniqueId', 'disableTransitions', 'transitionDurationScale']),
    appContainerClasses() {
      const appContainerClasses = [];

      if (this.disableTransitions) {
        appContainerClasses.push('app-no-transitions');
      }

      appContainerClasses.push(`transition-duration-scale-${this.transitionDurationScale.toString().replace('.', 'p')}`);

      if (this.background) {
        appContainerClasses.push(`background-${this.background}`);
      }

      console.log('appContainerClasses');

      return appContainerClasses;
    },
  },
  mounted() {
    const windowKeys = Object.keys(window);

    const surveyStore = useSurveyStore();

    surveyStore.setDisableTransitions(process.env.VUE_APP_DISABLE_TRANSITIONS === '1');

    if (process.env.VUE_APP_API_CLIENT_DEBUGGING) {
      this.$apiClient.setUseDebugConsole(true);
    }

    if (windowKeys.includes('submissionToken') && window.submissionToken) {
      surveyStore.setSubmissionToken(window.submissionToken, true);
    }

    if (windowKeys.includes('cliniqueId') && window.cliniqueId) {
      surveyStore.setCliniqueId(parseInt(window.cliniqueId, 10), true);
    }

    if (windowKeys.includes('cliniqueVerificationToken') && window.cliniqueVerificationToken) {
      surveyStore.setCliniqueVerificationToken(window.cliniqueVerificationToken, true);
    }

    if (windowKeys.includes('surveyIdentifier') && window.surveyIdentifier) {
      surveyStore.setSurveyIdentifier(window.surveyIdentifier, true);
    }

    if (windowKeys.includes('apiBaseUrl') && window.apiBaseUrl) {
      this.$apiClient.setBaseUrl(window.apiBaseUrl);
    }

    const submissionTokenFallbacks = [
      process.env.VUE_APP_STATIC_SUBMISSION_TOKEN, // If set in the env file, always use that
      this.submissionToken, // Otherwise use the one hopefully already set in the store
      // 'anonymous', // Default to anonymous
    ];

    let submissionToken;

    for (const submissionTokenSingle of submissionTokenFallbacks) {
      if (submissionTokenSingle) {
        submissionToken = submissionTokenSingle;

        break;
      }
    }

    this.$apiClient.setDefaultHeader('X-Language', 'en');

    if (windowKeys.includes('submissionTokenVerification') && window.submissionTokenVerification) {
      this.$apiClient.setDefaultHeader('X-Token-Verification', window.submissionTokenVerification);
    }

    if (submissionToken) {
      this.$apiClient.console.log(`Set submission token ${submissionToken}`);

      useSurveyStore().setSubmissionToken(submissionToken);
      this.$apiClient.setBearerToken(submissionToken);
    } else {
      this.$apiClient.console.log('Did not set a bearer token as non were defined');
    }

    this.$apiClient.setErrorHandler((error) => {
      this.$snackbar.add({
        type: 'error',
        text: error,
      });
    });
  },
  methods: {
    selectLanguage(locale) {
      const apiLanguagesTranslator = {
        da: 'dk',
        sv: 'se',
        nb: 'no',
      };

      const apiLanguage = apiLanguagesTranslator.hasOwnProperty(locale) ? apiLanguagesTranslator[locale] : locale;

      this.$apiClient.setDefaultHeader('X-Language', apiLanguage);

      this.$i18n.locale = locale;

      useSurveyStore().setLanguage(locale === 'system' ? 'en' : locale);

      this.selectedLanguage = true;

      // useSurveyStore().setBackground('grey');
    },
  },
};
</script>

<style lang="scss">
@import "sass/main.scss";

#app-container {
  background-repeat: no-repeat;
  background-position: top center;
  background-size: cover;
  background-attachment: fixed;
  background-color: #EDF1F4;
  position: fixed;
  top: 0;
  left: 0;
  overflow-y: auto;
  //padding-left: 20px;
  //padding-right: 2px;

  transition-property: width; // Irrelevant property background-color;
  transition-duration: 0ms !important;
  //transition-timing-function: ease-in-out;

  .foreground-overlay {
    z-index: 100000000000000;
    width: 100%;
    height: 100%;
    cursor: default;
    position: fixed;
    top: 0;
    left: 0;
  }

  // Backgrounds
  &.background-white {
    background-color: #F8F8F8;

    .foreground-overlay {
      background-color: #F8F8F8;
    }
  }

  &.background-grey {
    background-color: #EDF1F4;

    .foreground-overlay {
      background-color: #EDF1F4;
    }
  }

  &.background-transparent {
    background-color: transparent;

    .foreground-overlay {
      background-color: transparent;
    }
  }

  &.background-blue {
    background-color: #45A4CD;

    .foreground-overlay {
      background-color: #45A4CD;
    }
  }
}

ul {
  list-style: disc;
}

.language-button {
  cursor: pointer;

  transition-property: all;
  transition-duration: 250ms;
  transition-timing-function: ease-out;
  box-shadow: 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e, 0 0 #3e3e3e;
  transform: translateX(0) translateY(0);

  &.language-button-0 {
    &:hover {
      box-shadow: 1px 1px #3e3e3e, 2px 2px #3e3e3e, 3px 3px #3e3e3e, 4px 4px #3e3e3e, 5px 5px #3e3e3e, 6px 6px #3e3e3e, 7px 7px #3e3e3e, 8px 8px #3e3e3e;
      transform: translateX(-8px) translateY(-8px);
    }
  }

  &.language-button-1 {
    &:hover {
      box-shadow: -1px 1px #3e3e3e, -2px 2px #3e3e3e, -3px 3px #3e3e3e, -4px 4px #3e3e3e, -5px 5px #3e3e3e, -6px 6px #3e3e3e, -7px 7px #3e3e3e, -8px 8px #3e3e3e;
      transform: translateX(8px) translateY(-8px);
    }
  }

  &.language-button-2 {
    &:hover {
      box-shadow: 1px -1px #3e3e3e, 2px -2px #3e3e3e, 3px -3px #3e3e3e, 4px -4px #3e3e3e, 5px -5px #3e3e3e, 6px -6px #3e3e3e, 7px -7px #3e3e3e, 8px -8px #3e3e3e;
      transform: translateX(-8px) translateY(8px);
    }
  }

  &.language-button-3 {
    &:hover {
      box-shadow: -1px -1px #3e3e3e, -2px -2px #3e3e3e, -3px -3px #3e3e3e, -4px -4px #3e3e3e, -5px -5px #3e3e3e, -6px -6px #3e3e3e, -7px -7px #3e3e3e, -8px -8px #3e3e3e;
      transform: translateX(8px) translateY(8px);
    }
  }
}

.language-selector-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 320px;
  max-width: 90vw;

  > .row {
    > div {
      text-align: center;
    }
  }
}

.logo-container {
  text-align: center;
  margin-bottom: 30px;
}

@media print {
  #app-container {
    background-color: transparent !important;
    position: relative;
    overflow-y: visible;
    padding-bottom: 0 !important;
  }
}
</style>
